import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ScatterChart, Scatter
} from 'recharts';
import apiService from '../services/api';
import '../styles/components/HostSpecificAnalytics.css';

const HostSpecificAnalytics = ({ hostname = 'accipiter_gentilis' }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    summary: true,
    geographic: true,
    genetic: false,
    risk: false,
    mutations: false
  });

  useEffect(() => {
    fetchHostAnalytics();
  }, [hostname]);

  const fetchHostAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.request(
        `/monthly-reports/host/${encodeURIComponent(hostname)}/cumulative`
      );

      if (response?.success) {
        setAnalytics(response);
      } else {
        throw new Error('Failed to load host analytics');
      }
    } catch (err) {
      console.error('Error fetching host analytics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  if (loading) {
    return (
      <div className="host-analytics">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading {hostname} analytics...</p>
        </div>
      </div>
    );
  }

  if (error || !analytics) {
    const errorMessage = error?.includes('HTTP 404')
      ? `No data available for host: ${hostname}. This host may not have any H5N1 detections in the current dataset.`
      : error || 'No analytics data available for this host';

    return (
      <div className="host-analytics">
        <div className="error-container">
          <h3>Analytics Unavailable</h3>
          <p>{errorMessage}</p>
          <button onClick={fetchHostAnalytics} className="btn-retry">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const data = analytics || {};
  const summary = data.summary || {};
  const pathogenicity = data.pathogenicity_breakdown || [];
  const riskDist = data.risk_assessment?.distribution || [];
  const mutations = data.top_mutations || [];

  // Risk level colors
  const RISK_COLORS = {
    'CRITICAL': '#ef4444',
    'HIGH': '#f97316',
    'MEDIUM': '#eab308',
    'LOW': '#22c55e'
  };

  // Format hostname for display (e.g., accipiter_gentilis -> Accipiter gentilis)
  const displayHostname = (host) => {
    return host
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  return (
    <div className="host-analytics">
      <div className="analytics-header">
        <h2 className="analytics-title">{displayHostname(hostname)} - Detailed Analysis</h2>
        <p className="analytics-subtitle">Host-specific surveillance metrics and risk assessment</p>
      </div>

      {/* Summary Section */}
      <section className="analytics-section">
        <div className="section-header">
          <h3 className="section-title">Summary Overview</h3>
          <button
            className="expand-toggle"
            onClick={() => toggleSection('summary')}
            aria-label="Toggle summary"
          >
            {expandedSections.summary ? '−' : '+'}
          </button>
        </div>

        {expandedSections.summary && (
          <div className="section-content">
            <div className="summary-metrics">
              <div className="metric-box">
                <div className="metric-detail">
                  <div className="metric-label">Total Isolates</div>
                  <div className="metric-value">{summary.total_sequences || 0}</div>
                  <div className="metric-subtext">sequences detected</div>
                </div>
              </div>

              <div className="metric-box">
                <div className="metric-detail">
                  <div className="metric-label">Geographic Range</div>
                  <div className="metric-value">{summary.countries_represented || 0}</div>
                  <div className="metric-subtext">countries represented</div>
                </div>
              </div>

              <div className="metric-box">
                <div className="metric-detail">
                  <div className="metric-label">Genetic Diversity</div>
                  <div className="metric-value">{summary.clades_detected || 0}</div>
                  <div className="metric-subtext">distinct clades</div>
                </div>
              </div>
            </div>

            {data.risk_assessment?.avg_scores && Object.keys(data.risk_assessment.avg_scores).length > 0 && (
              <div className="ha-scores-box">
                <h4>HA Component Scores (Average)</h4>
                <div className="scores-grid">
                  {data.risk_assessment.avg_scores.avg_overall_ha && (
                    <div className="score-item">
                      <span className="score-label">Overall HA Score</span>
                      <span className="score-value">
                        {(data.risk_assessment.avg_scores.avg_overall_ha * 100).toFixed(1)}%
                      </span>
                    </div>
                  )}
                  {data.risk_assessment.avg_scores.avg_receptor_binding && (
                    <div className="score-item">
                      <span className="score-label">Receptor Binding</span>
                      <span className="score-value">
                        {(data.risk_assessment.avg_scores.avg_receptor_binding * 100).toFixed(1)}%
                      </span>
                    </div>
                  )}
                  {data.risk_assessment.avg_scores.avg_cleavage_site && (
                    <div className="score-item">
                      <span className="score-label">Cleavage Site</span>
                      <span className="score-value">
                        {(data.risk_assessment.avg_scores.avg_cleavage_site * 100).toFixed(1)}%
                      </span>
                    </div>
                  )}
                  {data.risk_assessment.avg_scores.avg_stability && (
                    <div className="score-item">
                      <span className="score-label">Stability</span>
                      <span className="score-value">
                        {(data.risk_assessment.avg_scores.avg_stability * 100).toFixed(1)}%
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      {/* Geographic Distribution */}
      {data.geographic_distribution && data.geographic_distribution.length > 0 && (
        <section className="analytics-section">
          <div className="section-header">
            <h3 className="section-title">Geographic Distribution</h3>
            <button
              className="expand-toggle"
              onClick={() => toggleSection('geographic')}
              aria-label="Toggle geographic"
            >
              {expandedSections.geographic ? '−' : '+'}
            </button>
          </div>

          {expandedSections.geographic && (
            <div className="section-content">
              <div className="chart-container">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={data.geographic_distribution}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                      dataKey="_id"
                      angle={-45}
                      textAnchor="end"
                      height={100}
                      tick={{ fontSize: 11 }}
                    />
                    <YAxis stroke="#6b7280" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#ffffff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px'
                      }}
                    />
                    <Bar dataKey="count" fill="#06b6d4" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="geographic-table">
                <h4>Detailed Country Breakdown</h4>
                <div className="table-wrapper">
                  {data.geographic_distribution.map((country, idx) => (
                    <div key={idx} className="table-row">
                      <span className="country-name">{country._id}</span>
                      <span className="country-count">
                        <span className="count-badge">{country.count}</span>
                        isolate{country.count !== 1 ? 's' : ''}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </section>
      )}

      {/* Genetic Composition */}
      {data.clade_distribution && data.clade_distribution.length > 0 && (
        <section className="analytics-section">
          <div className="section-header">
            <h3 className="section-title">Genetic Composition</h3>
            <button
              className="expand-toggle"
              onClick={() => toggleSection('genetic')}
              aria-label="Toggle genetic"
            >
              {expandedSections.genetic ? '−' : '+'}
            </button>
          </div>

          {expandedSections.genetic && (
            <div className="section-content">
              <div className="charts-row">
                <div className="chart-container half-width">
                  <h4>Clade Distribution</h4>
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={data.clade_distribution}
                        cx="50%"
                        cy="40%"
                        labelLine={false}
                        label={false}
                        outerRadius={60}
                        fill="#8884d8"
                        dataKey="count"
                      >
                        {['#0066cc', '#ff8b00', '#00a8a8', '#de350b', '#f59e0b', '#8b5cf6'].map((color, idx) => (
                          <Cell key={`cell-${idx}`} fill={color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => `${value} samples`} />
                      <Legend
                        layout="vertical"
                        align="right"
                        verticalAlign="middle"
                        formatter={(value, entry) => `${entry.payload._id}: ${entry.payload.count} (${((entry.payload.count / data.clade_distribution.reduce((sum, d) => sum + d.count, 0)) * 100).toFixed(1)}%)`}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {pathogenicity && pathogenicity.length > 0 && (
                  <div className="chart-container half-width">
                    <h4>Pathogenicity Type</h4>
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie
                          data={pathogenicity}
                          cx="50%"
                          cy="40%"
                          labelLine={false}
                          label={false}
                          outerRadius={60}
                          fill="#8884d8"
                          dataKey="count"
                        >
                          <Cell fill="#ef4444" />
                          <Cell fill="#22c55e" />
                          <Cell fill="#94a3b8" />
                        </Pie>
                        <Tooltip formatter={(value) => `${value} samples`} />
                        <Legend
                          layout="vertical"
                          align="right"
                          verticalAlign="middle"
                          formatter={(value, entry) => `${entry.payload._id || 'Unknown'}: ${entry.payload.count} (${((entry.payload.count / pathogenicity.reduce((sum, d) => sum + d.count, 0)) * 100).toFixed(1)}%)`}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>

              <div className="genetic-detail-box">
                <h4>Clade Lineages Detected</h4>
                <div className="lineage-list">
                  {data.clade_distribution.map((clade, idx) => (
                    <div key={idx} className="lineage-item">
                      <span className="lineage-rank">{idx + 1}</span>
                      <span className="lineage-name">{clade._id || 'Unknown'}</span>
                      <span className="lineage-count">{clade.count} isolates</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </section>
      )}

      {/* Risk Assessment */}
      {riskDist && riskDist.length > 0 && (
        <section className="analytics-section">
          <div className="section-header">
            <h3 className="section-title">Risk Assessment Distribution</h3>
            <button
              className="expand-toggle"
              onClick={() => toggleSection('risk')}
              aria-label="Toggle risk"
            >
              {expandedSections.risk ? '−' : '+'}
            </button>
          </div>

          {expandedSections.risk && (
            <div className="section-content">
              <div className="risk-chart-container">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={riskDist}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="_id" stroke="#6b7280" />
                    <YAxis stroke="#6b7280" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#ffffff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px'
                      }}
                    />
                    <Legend />
                    <Bar
                      dataKey="count"
                      name="Number of Isolates"
                      fill="#8884d8"
                      radius={[8, 8, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="risk-breakdown">
                <h4>Risk Level Summary</h4>
                <div className="risk-items">
                  {riskDist.map((item, idx) => (
                    <div key={idx} className="risk-item">
                      <div
                        className="risk-color-box"
                        style={{ backgroundColor: RISK_COLORS[item._id] || '#6b7280' }}
                      ></div>
                      <div className="risk-info">
                        <span className="risk-level">{item._id}</span>
                        <span className="risk-count">{item.count} isolates</span>
                        {item.avg_score && (
                          <span className="risk-score">Avg score: {item.avg_score.toFixed(2)}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </section>
      )}

      {/* Critical Mutations */}
      {mutations && mutations.length > 0 && (
        <section className="analytics-section">
          <div className="section-header">
            <h3 className="section-title">Critical Mutations Detected</h3>
            <button
              className="expand-toggle"
              onClick={() => toggleSection('mutations')}
              aria-label="Toggle mutations"
            >
              {expandedSections.mutations ? '−' : '+'}
            </button>
          </div>

          {expandedSections.mutations && (
            <div className="section-content">
              <p className="section-description">
                These mutations have been identified in {hostname} isolates and are monitored for pandemic potential.
              </p>

              <div className="mutations-chart-container">
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart
                    data={mutations}
                    layout="vertical"
                    margin={{ top: 20, right: 30, left: 200, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis type="number" stroke="#6b7280" />
                    <YAxis dataKey="_id" type="category" width={190} tick={{ fontSize: 11 }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#ffffff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px'
                      }}
                    />
                    <Legend />
                    <Bar dataKey="count" name="Occurrences" fill="#ef4444" radius={[0, 8, 8, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="mutations-detail-list">
                <h4>Detailed Mutation Information</h4>
                {mutations.slice(0, 10).map((mutation, idx) => (
                  <div key={idx} className="mutation-detail-item">
                    <div className="mutation-header">
                      <span className="mutation-rank">#{idx + 1}</span>
                      <span className="mutation-name">{mutation._id}</span>
                      <span className="mutation-count-badge">{mutation.count} samples</span>
                    </div>
                    {mutation.risk_score && (
                      <div className="mutation-risk">
                        <span className="risk-label">Risk Score:</span>
                        <span className="risk-value">{mutation.risk_score.toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {/* Footer Note */}
      <section className="analytics-section info-section">
        <div className="info-box">
          <h4>About This Analysis</h4>
          <p>
            This host-specific analysis provides detailed surveillance data for {hostname} H5N1 detections.
            It includes genetic diversity, geographic distribution, pathogenicity characteristics, and critical mutations
            that may affect pandemic potential. Data is sourced from GISAID and processed through computational genomics methods.
          </p>
        </div>
      </section>
    </div>
  );
};

export default HostSpecificAnalytics;
